import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { PatternFormat } from "react-number-format";
import { Player } from "@lottiefiles/react-lottie-player";
import Cards from "react-credit-cards-2";
import "react-credit-cards-2/dist/es/styles-compiled.css";

import {
  IconCreditCard,
  IconUser,
  IconCalendarMonth,
  IconCalendar,
  IconLock,
} from "@tabler/icons-react";

import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { AppStore } from "../../../../redux/store";
import useCallApiAndLoad from "../../../../hooks/useCallApiAndLoad";
import { OnePayPayload, CroemPaymentPayload } from "../../../../models/paymentModel";
import FormControlError from "../../../../components/Controls/FormControlError";
import ErrorForm from "../../../../components/Controls/ErrorForm";
import { fetchResponseAdapter } from "../../../../adapters/fetchAdapter";
import { sendOnePayEndpoint } from "../../../Investor/services/investorService";
import CroemCardMethodPayment from "./CroemCardMethodPayment";
import { trackPayment, trackPaymentAndWait, PaymentTrackingContext } from "../../../../lib/trackPayment";
import { PaymentEvents } from "../../../../lib/paymentEvents";
import { sanitizePaymentRequest } from "../../../../lib/paymentTrackingSanitizer";
import { sanitizeErrorPayload } from "../../../../lib/errorPayload";

interface PaymentForm {
  name_card: string;
  lastname_card: string;
  number_card: string;
  month: string;
  year: string;
  cvv: string;
}

interface CardProviderInfo {
  provider_logo: string;
  provider_name?: string;
}

interface Props {
  name?: string;
  btnText?: string;
  contractId: string;
  transactionId: string;
  realm: string;
  cardProviderInfo?: CardProviderInfo;
  tracking?: PaymentTrackingContext;
  // Device Fingerprint: solo llega con valor en Guatemala.
  deviceFingerprintId?: string;
}

// Datos seguros del path CROEM guest: el payload arma un OnePayPayload con token
// adentro, así que el provider se fija explícito (el sanitizer lo vería onepay).
const CROEM_SAFE = {
  payment_method: "card" as const,
  card_provider: "croem" as const,
  card_last4: null,
  has_token: true,
};

const CardMethodPayment = (props: Props) => {
  const { country } = useSelector((store: AppStore) => store.country);
  const { ipClient } = useSelector((store: AppStore) => store.ipClient);
  const { callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();

  const [sendButton, setSendButton] = useState(false);
  const [formError, setFormError] = useState("");
  const [loadingPay, setLoadingPay] = useState(false);
  const [threeDsUrl, setThreeDsUrl] = useState<string>("");
  const [showThreeDs, setShowThreeDs] = useState(false);

  const isCroemProvider = props.cardProviderInfo?.provider_name === "CROEM";
  // CROEM_SAFE es constante de módulo; el fingerprint llega por props.
  const croemSafe = {
    ...CROEM_SAFE,
    device_fingerprint_id: props.deviceFingerprintId,
  };
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<PaymentForm>({
    resolver: yupResolver(
      yup
        .object({
          name_card: yup.string().required("Nombre es requerido"),
          lastname_card: yup.string().required("Apellido es requerido"),
          number_card: yup
            .string()
            .required("Numero de la tarjeta es requerido"),
          month: yup
            .string()
            .required("El mes es requerido")
            .test(
              "is-future-month",
              "El mes no puede ser anterior al actual",
              function (value) {
                if (value === undefined || value === null) {
                  return false;
                }
                const monthValue = parseInt(value, 10);
                if (currentYear === parseInt(this.parent.year, 10)) {
                  return monthValue >= currentMonth;
                }
                return true;
              }
            ),
          year: yup
            .string()
            .required("El año es requerido")
            .test(
              "is-future-year",
              "El año no puede ser anterior al actual",
              function (value) {
                if (value === undefined || value === null) {
                  return false;
                }
                const yearValue = parseInt(value, 10);
                return yearValue >= currentYear;
              }
            ),
          cvv: yup.string().required("El cvv es requerido"),
        })
        .required()
    ),
  });

  /* ===============================
        ONEPAY NORMAL
  =============================== */

  const sendData = async (form: PaymentForm) => {
    setFormError("");

    const payload: OnePayPayload = {
      cardInfo: {
        cardNumber: form.number_card,
        expireMonth: form.month,
        expireYear: form.year,
        cvv: form.cvv,
        cardOwnerName: form.name_card,
        cardOwnerLastName: form.lastname_card,
      },
      clientIp: ipClient,
      device_fingerprint_id: props.deviceFingerprintId,
    };
    setSendButton(true);
    return sendTenantPayment(payload);
  };

  const sendTenantPayment = async (payload: OnePayPayload) => {
    setLoadingPay(true);

    const safe = sanitizePaymentRequest(payload as any);
    trackPayment(PaymentEvents.Submitted, { ...props.tracking, ...safe });
    const { status, data } = await callEndpoint(
      sendOnePayEndpoint(payload, props.transactionId)
    );

    setLoadingPay(false);
    setSendButton(false);

    if (status === 201) {
      trackPayment(PaymentEvents.Completed, { ...props.tracking, ...safe });
      document.body.style.overflowY = "scroll";
      return navigate(`/app/${country}/pago-rapido/exito`);
    }

    const response = fetchResponseAdapter(data);
    trackPayment(PaymentEvents.Failed, {
      ...props.tracking,
      ...safe,
      reason: "authorization_declined",
      error_code: response.code,
      request_payload: JSON.stringify(safe),
      response_payload: sanitizeErrorPayload(data),
    });
    setFormError(`(${response.code}) ${response.message}`);
  };

  /* ===============================
        CROEM PAYMENT
  =============================== */

  const sendCroemPayment = async (
    token: string,
    cardOwnerName: string,
    cardOwnerLastName: string,
    cvv: string
  ) => {
    setLoadingPay(true);

    const payload: OnePayPayload = {
      cardInfo: {
        token,
        cvv,
        cardOwnerName,
        cardOwnerLastName,
      },
      clientIp: ipClient,
      device_fingerprint_id: props.deviceFingerprintId,
    };

    trackPayment(PaymentEvents.Submitted, { ...props.tracking, ...croemSafe });
    const { status, data } = await callEndpoint(
      sendOnePayEndpoint(payload as any, props.transactionId)
    );

    setLoadingPay(false);
    setSendButton(false);

    // ✅ SUCCESS SIN 3DS
    if (status === 201 && data?.isTdsChallenge === false) {
      trackPayment(PaymentEvents.Completed, { ...props.tracking, ...croemSafe });
      document.body.style.overflowY = "scroll";
      return navigate(`/app/${country}/pago-rapido/exito`);
    }

    // ✅ REQUIERE 3DS CHALLENGE
    if (status === 201 && data?.data?.isTdsChallenge === true) {
      //setThreeDsUrl(data.data.returnUrl);
      //setShowThreeDs(true);
      // await: el redirect a returnUrl descarga la página; esperamos la
      // confirmación del envío (o el timeout de seguridad) antes del unload.
      await trackPaymentAndWait(PaymentEvents.ThreeDsChallenged, {
        ...props.tracking,
        ...croemSafe,
      });
      window.location.href = data.data.returnUrl;
      return;
    }

    // ❌ ERROR
    const response = fetchResponseAdapter(data);
    trackPayment(PaymentEvents.Failed, {
      ...props.tracking,
      ...croemSafe,
      reason: "authorization_declined",
      error_code: response.code,
      request_payload: JSON.stringify(croemSafe),
      response_payload: sanitizeErrorPayload(data),
    });
    setFormError(`(${response.code}) ${response.message}`);
  };

  const handle3DsSuccess = () => {
    setShowThreeDs(false);
    document.body.style.overflowY = "scroll";
    navigate(`/app/${country}/pago-rapido/exito`);
  };

  const handle3DsError = (error: string) => {
    setShowThreeDs(false);
    setFormError(error);
  };

  const handle3DsClose = () => {
    setShowThreeDs(false);
    // ✅ ALTERNATIVA: Redirigir directamente a la URL
    if (threeDsUrl) {
      window.location.href = threeDsUrl;
    }
  };

  return (
    <div className="w-full bg-white overflow-y-auto relative">
{/* ✅ LOADING OVERLAY */}
{loadingPay && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
    <div className="bg-white py-8 px-8 rounded-lg w-full max-w-sm">
      <Player
        speed={1}
        autoplay
        loop
        src="https://storage.googleapis.com/assets-us-east4-propilatam-dev/website-assets/propi-web/loading-propi1-verde.json"
        style={{ height: "80px", width: "80px" }}
      />
      <h1 className="text-2xl font-semibold text-[#212121] mt-4 text-center">
        Procesando tu pago
      </h1>
      <p className="text-sm font-medium text-[#757575] text-center">
        Por favor espera unos segundos y no cierres la pantalla.
      </p>
    </div>
  </div>
)}

      {/* ===============================
          SWITCH ENTRE PROVIDERS
      =============================== */}

      {isCroemProvider ? (
        <CroemCardMethodPayment
          formError={formError}
          btnText={props.btnText || "Pagar"}
          onActionCancel={() => window.history.back()}
          onTokenReceived={(token, name, lastName, cvv) => {
            setLoadingPay(true);
            sendCroemPayment(token, name, lastName, cvv);
          }}
          onError={(error) => {
            // El widget guest enruta la cancelación por onError con este string
            // fijo (no tiene onCancel propio). Lo usamos para separar abandono
            // de error de tokenización sin cambiar el comportamiento existente.
            const isCancel = error === "Usuario canceló la operación";
            trackPayment(isCancel ? PaymentEvents.Cancelled : PaymentEvents.Failed, {
              ...props.tracking,
              payment_method: "card",
              card_provider: "croem",
              card_last4: null,
              ...(isCancel ? {} : { reason: "tokenization_error" }),
            });
            setFormError(error);
            setLoadingPay(false);
          }}
        />
      ) : (
        <form onSubmit={handleSubmit(sendData)}>
          <div className="w-full bg-white px-0 lg:px-4">
            <div className="w-full py-4 ">
              <h2 className="text-[#212121] font-semibold text-lg tracking-normal">
                Informacion de pago
              </h2>
              <div className="mt-3">
                <Cards
                  number={watch("number_card") ? watch("number_card") : ""}
                  expiry={`${watch("month") ? watch("month") : ""}/${
                    watch("year") ? watch("year") : ""
                  }`}
                  cvc={watch("cvv") ? watch("cvv") : ""}
                  name={`${watch("name_card") ? watch("name_card") : ""} ${
                    watch("lastname_card") ? watch("lastname_card") : ""
                  }`}
                />
              </div>
              {formError && <FormControlError message={formError} />}
            </div>
            <div className="my-2">
              <label className="text-[#212121] font-semibold text-sm">
                Nombre del tarjetahabiente
              </label>
              <div className="relative input-icon">
                <IconUser color="#BDBDBD" />
                <input
                  {...register("name_card")}
                  type="text"
                  placeholder="Ej: Juan"
                  className="w-full focus:outline-none mt-1 border border-grayForm rounded-md text-graySoft py-2 px-2 pl-10 "
                />
              </div>
              <ErrorForm
                message={errors?.name_card?.message}
                additionalClass="mb-3"
              />
            </div>

            <div className="my-2">
              <label className="text-[#212121] font-semibold text-sm">
                Apellido del tarjetahabiente
              </label>
              <div className="relative input-icon">
                <IconUser color="#BDBDBD" />
                <input
                  {...register("lastname_card")}
                  type="text"
                  placeholder="Ej: Perez"
                  className="w-full focus:outline-none mt-1 border border-grayForm rounded-md text-graySoft py-2 px-2 pl-10 "
                />
              </div>
              <ErrorForm
                message={errors?.lastname_card?.message}
                additionalClass="mb-3"
              />
            </div>

            <div className="my-2">
              <label className="text-[#212121] font-semibold text-sm">
                Número de la tarjeta
              </label>
              <div className="relative input-icon">
                <IconCreditCard color="#bdbdbd" />
                <Controller
                  control={control}
                  name="number_card"
                  render={({ field: { onChange, value, onBlur } }) => (
                    <PatternFormat
                      placeholder="000-000-000-000"
                      onValueChange={(v) => onChange(v.value)}
                      className="w-full focus:outline-none mt-1 border border-grayForm rounded-md text-graySoft py-2 px-2 pl-10"
                      format="#### #### #### #### ###"
                      max={19}
                      onBlur={onBlur}
                    />
                  )}
                />
              </div>
              <ErrorForm
                message={errors?.number_card?.message}
                additionalClass="mb-3"
              />
            </div>

            <div className="w-full flex items-center justify-between flex-wrap">
              <div className="w-full lg:w-1/3 px-1 my-1 lg:my-0">
                <label className="text-[#212121] font-semibold text-sm">
                  Mes
                </label>
                <div className="relative input-icon">
                  <IconCalendarMonth color="#BDBDBD" />
                  <Controller
                    control={control}
                    name="month"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <PatternFormat
                        placeholder="Ej: 09"
                        onValueChange={(v) => onChange(v.value)}
                        className="w-full focus:outline-none mt-1 border border-grayForm rounded-md text-graySoft py-2 px-2 pl-10"
                        format="##"
                        onBlur={onBlur}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="w-full lg:w-1/3 px-1 my-1 lg:my-0">
                <label className="text-[#212121] font-semibold text-sm">
                  Año
                </label>
                <div className="relative input-icon">
                  <IconCalendar color="#BDBDBD" />
                  <Controller
                    control={control}
                    name="year"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <PatternFormat
                        placeholder="Ej: 2022"
                        onValueChange={(v) => onChange(v.value)}
                        className="w-full focus:outline-none mt-1 border border-grayForm rounded-md text-graySoft py-2 px-2 pl-10"
                        format="####"
                        onBlur={onBlur}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="w-full lg:w-1/3 px-1 my-1 lg:my-0">
                <label className="text-[#212121] font-semibold text-sm">
                  CVV2
                </label>
                <div className="relative input-icon">
                  <IconLock color="#bdbdbd" />
                  <Controller
                    control={control}
                    name="cvv"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <PatternFormat
                        onValueChange={(v) => onChange(v.value)}
                        className="w-full focus:outline-none mt-1 border border-grayForm rounded-md text-graySoft py-2 px-2 pl-10"
                        format="####"
                        max={4}
                        // Enmascarado: el CVV no debe quedar a la vista de
                        // quien mire la pantalla del comprador.
                        type="password"
                        autoComplete="cc-csc"
                        // mask="" es obligatorio junto a type="password": sin
                        // esto la librería rellena los huecos del format con
                        // espacios, y el input los enmascara igual que a los
                        // dígitos — se verían 4 símbolos con un solo dígito.
                        mask=""
                      />
                    )}
                  />
                </div>
              </div>

              <div className="w-full">
                <ErrorForm
                  message={errors?.month?.message}
                  additionalClass="mb-3"
                />
                <ErrorForm
                  message={errors?.year?.message}
                  additionalClass="mb-3"
                />
                <ErrorForm
                  message={errors?.cvv?.message}
                  additionalClass="mb-3"
                />
              </div>
            </div>
          </div>

          <div className="bg-white w-full">
            <div className="w-full grid grid-cols-1 gap-4 lg:px-4 lg:py-4 mt-4">
              <button
                disabled={sendButton}
                type="submit"
                className={`${
                  sendButton ? "opacity-60" : ""
                } bg-[#212121] py-3 xl:py-3 w-auto text-white text-base rounded-lg flex justify-center items-center`}
              >
                <span className="">{props.btnText}</span>
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default CardMethodPayment;
